import React, { useRef, useEffect, useState } from "react";
import { CertificateFormStyled } from "./styled";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IMG_PATH } from "utils/constants/imgPath";
import { Buttons, Cards, Inputs } from "components";

const CertificateForm = ({
  initialValues,
  permission,
  activitiOption,
  activityOption,
  linkedActivity,
  onCancel,
  onDelete,
  onSubmit,
}) => {
  const schema = yup.object().shape({
    ref_type: yup.string().required("Required"),
    certificate_code: yup.string().required("Required"),
    certificate_name: yup.string().required("Required"),
    activity_ref_id: yup.string().required("Required"),
    sub_category: yup.string().required("Required"),
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchRefType = watch("ref_type");
  const watchExpireDate = watch("expire_date");
  const isActivityRef = watchRefType === "2";
  const isEdit = !!initialValues;
  // สลับชนิดตอนสร้าง ต้องล้างกิจกรรมที่เลือกไว้ ไม่งั้นค่าข้ามชนิดค้างแล้วบันทึกพัง
  useEffect(() => {
    if (!isEdit) {
      setValue("activity_ref_id", "");
    }
  }, [watchRefType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialValues) {
      if (initialValues.img_path) {
        setTemplate(IMG_PATH + initialValues.img_path);
      }
      setFontSize(initialValues.cus_name_font_size);
      setPosition({ ...position, y: initialValues.cus_name_position_y });
      reset({
        ...initialValues,
        // API คืนมาเป็น ISO string แต่ DateTime ต้องการ Date object
        expire_date: initialValues.expire_date
          ? new Date(initialValues.expire_date)
          : null,
        img_file: initialValues.img_path
          ? [{ media_uri: initialValues.img_path }]
          : [],
      });
    }
  }, [initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const _onSubmit = (values) => {
    let isErr = false;
    if (values.status === "2" && !values.rejection_reason) {
      isErr = true;
      setError("rejection_reason", {
        type: "custom",
        message: "Required",
      });
    }
    if (!isErr) {
      onSubmit(values);
    }
  };

  const [template, setTemplate] = useState(null); // เก็บ URL ของ Template
  const [name, setName] = useState("Your Name");
  const [position, setPosition] = useState({ x: 200, y: 300 });
  const [fontSize, setFontSize] = useState(24);
  const canvasRef = useRef(null);

  // ฟังก์ชันอัปโหลดภาพ Template
  const handleTemplateUpload = (e) => {
    const file = e[e.length - 1];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setTemplate(e.target.result); // เก็บ Base64 URL ของภาพ
      reader.readAsDataURL(file);
    }
  };

  // เรนเดอร์ Canvas เมื่อ Template หรือข้อมูลอื่น ๆ เปลี่ยนแปลง
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // ล้าง Canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (template) {
      const img = new Image();
      img.crossOrigin = "anonymous"; // อนุญาต Cross-Origin
      img.src = template;

      // รอให้ภาพโหลดสำเร็จก่อน
      img.onload = () => {
        // ปรับขนาด canvas ให้เหมาะสมกับขนาดของรูป
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height); // วาด Background ให้เต็มพื้นที่
        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = "black";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle"; // ตั้งค่าตำแหน่งข้อความกลาง
        // ctx.fillText(name, position.x, position.y); // วาดชื่อในตำแหน่งที่กำหนด
        ctx.fillText(name, img.width / 2, position.y); // วาดชื่อในตำแหน่งที่กำหนด
      };
    }
  }, [template, name, position, fontSize]);

  // ฟังก์ชันดาวน์โหลด Certificate
  const handleDownload = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "certificate.png";
    link.href = canvas.toDataURL();

    link.click();
  };

  return (
    <CertificateFormStyled isShowCanva={template}>
      <form onSubmit={handleSubmit(_onSubmit)}>
        <div className="form_section">
          <div className="form_title">Certificate Info</div>
          <div className="input_row">
            <div className="f_left_col">Certificate For</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.Dropdown
                    {...field}
                    options={[
                      { value: "1", label: "Online Form" },
                      { value: "2", label: "Activity" },
                    ]}
                    // ล็อกหลังสร้าง ฝั่ง API ก็ตัด ref_type ทิ้งตอน update
                    // เพราะสลับชนิดแปลว่าต้องย้าย back-reference ข้าม collection
                    disabled={isEdit}
                    errors={errors.ref_type?.message}
                  />
                )}
                name="ref_type"
                defaultValue="1"
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Certificate Code</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    // placeholder={t('test')}
                    errors={errors.certificate_code?.message}
                  />
                )}
                name="certificate_code"
                defaultValue=""
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Certificate Name</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    // placeholder={t('test')}
                    errors={errors.certificate_name?.message}
                  />
                )}
                name="certificate_name"
                defaultValue=""
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">
              {isActivityRef ? "For Activity" : "For Online Form"}
            </div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.DropdownSearch
                    {...field}
                    options={
                      (isActivityRef ? activityOption : activitiOption) || []
                    }
                    placeholder="Select..."
                    errors={errors.activity_ref_id?.message}
                    // disabled={initialValues}
                  />
                )}
                name="activity_ref_id"
              />
              {isActivityRef && (
                <div>
                  ***แสดงเฉพาะกิจกรรม Offline ที่ตั้ง Condition Type = สแกน
                  qrcode ระหว่างงาน และ Gift Type = Certificate
                </div>
              )}
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Sub Category Name</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    // placeholder={t('test')}
                    errors={errors.sub_category?.message}
                  />
                )}
                name="sub_category"
                defaultValue=""
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">วันหมดอายุ</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.DateTime
                    {...field}
                    placeholder={"ไม่มีวันหมดอายุ"}
                    errors={errors.expire_date?.message}
                    showYearDropdown
                  />
                )}
                name="expire_date"
              />
              {watchExpireDate && (
                <button
                  type="button"
                  className="btn_clear_date"
                  onClick={() => setValue("expire_date", null)}
                >
                  ล้างวันหมดอายุ (กลับไปเป็นไม่มีวันหมดอายุ)
                </button>
              )}
              <div>
                ***เลือกแล้วจะรับใบได้ถึงสิ้นวันของวันที่เลือก
                หลังจากนั้นสแกนรับใบไม่ได้ (ใบที่ออกไปแล้วยังโหลดได้ตามปกติ)
              </div>
            </div>
          </div>
        </div>
        <div className="form_section">
          <div className="form_title">Certificate Generator</div>
          <div className="input_row">
            <div className="f_left_col">Certificate Picture</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.UploadImage
                    {...field}
                    theme_upload_image
                    fieldName="img_path"
                    errors={errors.img_file?.message}
                    onChangeCustom={handleTemplateUpload}
                  />
                )}
                name="img_file"
                defaultValue={[]}
              />
              <div>***รูป JPG or PNG ขนาดกว้าง 3508 px สูง 2480 px</div>
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Sample Name</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    // placeholder={t('test')}
                    errors={errors.name_exam?.message}
                    onChangeCustom={(e) => setName(e.target.value)}
                  />
                )}
                name="name_exam"
                defaultValue="Your Name"
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Font Size</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    type="Number"
                    // placeholder={t('test')}
                    errors={errors.cus_name_font_size?.message}
                    onChangeCustom={(e) => setFontSize(Number(e.target.value))}
                  />
                )}
                name="cus_name_font_size"
                defaultValue={24}
              />
            </div>
          </div>
          <div className="input_row">
            <div className="f_left_col">Position Y</div>
            <div className="f_right_col">
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.TextField
                    {...field}
                    type="Number"
                    // placeholder={t('test')}
                    errors={errors.cus_name_position_y?.message}
                    onChangeCustom={(e) =>
                      setPosition({ ...position, y: Number(e.target.value) })
                    }
                  />
                )}
                name="cus_name_position_y"
                defaultValue={300}
              />
            </div>
          </div>
          {template && (
            <div className="input_row">
              <div className="f_left_col"></div>
              <div className="f_right_col">
                <div className="btn_download">
                  <Buttons.BgStandard
                    label={"Download Sample"}
                    onClick={handleDownload}
                  />
                </div>
              </div>
            </div>
          )}
          <canvas ref={canvasRef} className="certificate_canva" />
        </div>
        {/* ใบของ activity แสดง qrcode ของกิจกรรมไว้ที่นี่ด้วย
            แอดมินจะได้หยิบไปปริ้นท์ได้จากทั้ง 2 เมนู ไม่ต้องเด้งไปมา
            ต้องใช้รูปแบบ url เดียวกับหน้า Activity เป๊ะ ไม่งั้นสแกนแล้วไม่เจอกิจกรรม */}
        {/* Online Form: 1 ฟอร์มมีได้หลายใบ แต่ละใบมี QR ของตัวเอง
            แอดมินเป็นคนเลือกว่าจะเปิด QR ใบไหนให้นักเรียนคนไหนสแกน
            QR ผูกกับ "ใบ" ไม่ใช่ฟอร์ม จึงใช้ certificate._id ตรง ๆ */}
        {!isActivityRef && initialValues && initialValues._id && (
          <div className="form_section">
            <div className="form_title">
              QRCode สำหรับสแกนเพื่อรับ Certificate
              {/* QRCode สำหรับรับใบนี้
              {initialValues.certificate_name
                ? ` : ${initialValues.certificate_name}`
                : ""} */}
            </div>
            <div className="cf_qrcode_row">
              <Cards.QrCodeItem
                code={`${process.env.REACT_APP_DOMAIN}?redirect=certificate-scan&id=${initialValues._id}`}
                label={initialValues.certificate_code}
              />
            </div>
            <div>
              ** สำหรับผู้สมัครที่ลงทะเบียน Online Form
              สำเร็จและแอดมินอนุมัติแล้วเท่านั้น จึงจะสามารถสแกน QRcode
              นี้เพื่อรับ Certificate ได้ (ผู้สมัครหนึ่งคนไม่สามารถสแกนซ้ำ
              Certificate ใบเดิมได้ แต่สามารถสแกนเพื่อรับ Certificate
              อื่นที่อยู่ในลิงค์กับ Online Form นี้ได้
            </div>
          </div>
        )}
        {isActivityRef &&
          linkedActivity &&
          linkedActivity.qrcode_list?.length > 0 && (
            <div className="form_section">
              <div className="form_title">
                QRCode ของกิจกรรม
                {/* ใช้ name_th จากตัวกิจกรรมโดยตรง ไม่ใช้ certificate.activity_name
                    เพราะถ้าแอดมินไปแก้ชื่อกิจกรรมทีหลัง ค่าที่ snapshot ไว้บนใบจะค้างของเก่า */}
                {linkedActivity.name_th ? ` : ${linkedActivity.name_th}` : ""}
                {linkedActivity.code ? ` (${linkedActivity.code})` : ""}
              </div>
              <div className="cf_qrcode_row">
                {linkedActivity.qrcode_list.map((e, i) => (
                  <div key={i}>
                    <Cards.QrCodeItem
                      code={
                        linkedActivity.condition_type === "5"
                          ? `${process.env.REACT_APP_DOMAIN}?redirect=activity-scan&id=${linkedActivity._id}&code=${e}`
                          : e
                      }
                      label={e}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        {permission && (
          <div className="cf_action_row">
            <div className="cf_action_item">
              <Buttons.BgStandard theme_gray label="Back" onClick={onCancel} />
            </div>
            {initialValues && permission.is_delete && (
              <div className="cf_action_item">
                <Buttons.BgStandard
                  theme_red
                  label="Delete"
                  onClick={onDelete}
                />
              </div>
            )}
            {initialValues && permission.is_update ? (
              <>
                <div className="cf_action_item">
                  <Buttons.BgStandard
                    type="submit"
                    label="Update"
                    // disabled={initialValues && initialValues.is_center}
                  />
                </div>
              </>
            ) : (
              permission.is_create && (
                <div className="cf_action_item">
                  <Buttons.BgStandard
                    type="submit"
                    label="Save"
                    // disabled={initialValues && initialValues.is_center}
                  />
                </div>
              )
            )}
          </div>
        )}
      </form>
    </CertificateFormStyled>
  );
};

CertificateForm.propTypes = {};

export default CertificateForm;
